/**
 * 표 열 정렬의 공통 규칙. (2026-08-28)
 *
 * 저점매수 감시 대상(bandDisplay)에서 시작한 로직인데 섹터 표에도
 * 정렬이 필요해져서(사용자 요청) 두 번째 사용처가 생겼다.
 * 규약 5.2(2회 이상이면 공통화)에 따라 제네릭으로 뽑았다.
 *
 * 순수 함수만 있다 - 컴포넌트 없이 그대로 테스트한다.
 */

export interface SortState<K extends string> {
  key: K | null;
  /** asc = 작은 것부터. key 가 null 이면 무시된다 */
  dir: 'asc' | 'desc';
}

/**
 * 열을 눌렀을 때의 다음 정렬 상태.
 *
 * 같은 열을 다시 누르면 방향이 뒤집히고, 세 번째에는 기본 순서로 돌아온다.
 * 두 번에서 멈추면 "원래대로 돌리는 법" 이 없어 새로고침을 하게 된다.
 */
export function nextSort<K extends string>(sort: SortState<K>, key: K): SortState<K> {
  if (sort.key !== key) {
    return { key, dir: 'asc' };
  }
  if (sort.dir === 'asc') {
    return { key, dir: 'desc' };
  }
  return { key: null, dir: 'asc' };
}

/**
 * 행 정렬.
 *
 * 🔴 원본을 바꾸지 않는다 - TanStack Query 캐시 배열을 제자리 정렬하면
 *   다음 렌더의 비교가 어긋난다.
 *
 * 값이 없는 행(시세 없음, 시가총액 미수집 등)은 어느 방향이든 맨 아래다 -
 * null 을 0 으로 치면 "0%" 나 "0원" 인 진짜 행과 구분되지 않는다.
 *
 * @param value 열 키에 해당하는 행의 숫자 값. 없으면 null
 * @param base  기본 순서 비교자. key 가 null 일 때와 값이 같을 때 쓴다
 */
export function sortRows<T, K extends string>(
  rows: T[],
  sort: SortState<K>,
  value: (row: T, key: K) => number | null,
  base: (a: T, b: T) => number,
): T[] {
  const { key } = sort;
  if (key === null) {
    return [...rows].sort(base);
  }

  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = value(a, key);
    const bv = value(b, key);
    if (av === null && bv === null) {
      return base(a, b);
    }
    if (av === null) {
      return 1;
    }
    if (bv === null) {
      return -1;
    }
    if (av === bv) {
      return base(a, b);
    }
    return (av - bv) * sign;
  });
}

/** 정렬 표시자. 열 머리에 붙는다. */
export function sortIndicator<K extends string>(sort: SortState<K>, key: K): string {
  if (sort.key !== key) {
    return '';
  }
  return sort.dir === 'asc' ? ' ▲' : ' ▼';
}
